import React, { useMemo } from 'react';
import { Card, Empty, Spin } from 'antd';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const MODEL_COLORS = {
  'Total': '#000000',
  'gpt-4': '#1890ff',
  'gpt-3.5': '#52c41a',
  'claude': '#722ed1',
  'mistral': '#eb2f96',
  'llama': '#fa541c',
  'anthropic': '#13c2c2',
  'gemini': '#faad14',
  'meta-llama': '#597ef7',
  'perplexity': '#f759ab'
};

// Fallback colors for any models not in the predefined list
const FALLBACK_COLORS = [
  '#69c0ff', '#95de64', '#ff7a45', '#b37feb', '#5cdbd3',
  '#40a9ff', '#95de64', '#ffa940', '#9254de', '#36cfc9'
];

const CostAnalyticsChart = ({ 
  data,
  displayMetric = 'cost',
  selectedModels = [],
  loading = false,
  onModelSelect
}) => {
  const { chartData, models } = useMemo(() => {
    if (!data?.daily_costs || !Array.isArray(data.daily_costs)) {
      return { chartData: [], models: [] };
    }

    // Get unique models and sort by total usage
    const modelTotals = {};
    data.daily_costs.forEach(entry => {
      if (entry.model) {
        const value = displayMetric === 'cost' 
          ? Number(entry.cost || 0)
          : displayMetric === 'calls' 
            ? Number(entry.api_calls || 0)
            : Number(entry.prompt_tokens || 0) + Number(entry.completion_tokens || 0);
        
        modelTotals[entry.model] = (modelTotals[entry.model] || 0) + value;
      }
    });

    const models = Object.entries(modelTotals)
      .sort(([, a], [, b]) => b - a)
      .map(([model]) => model);

    // Group by date
    const dateGroups = {};
    data.daily_costs.forEach(entry => {
      if (!entry.date || !entry.model) return;
      
      if (!dateGroups[entry.date]) {
        dateGroups[entry.date] = {
          date: entry.date,
          total: 0,
          ...models.reduce((acc, model) => ({ ...acc, [model]: 0 }), {})
        };
      }

      const value = displayMetric === 'cost' 
        ? Number(entry.cost || 0)
        : displayMetric === 'calls' 
          ? Number(entry.api_calls || 0)
          : Number(entry.prompt_tokens || 0) + Number(entry.completion_tokens || 0);

      dateGroups[entry.date][entry.model] = value;
      dateGroups[entry.date].total += value;
    });

    // Convert to array and sort by date
    const chartData = Object.values(dateGroups)
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    return { chartData, models };
  }, [data, displayMetric]);

  const handleLegendClick = (e) => {
    if (e.value === 'Total') return; // Don't handle Total clicks
    onModelSelect?.(e.value);
  };

  const handleChartClick = (e) => {
    // If clicking on the chart container but not on a specific element
    if (e.target === e.currentTarget) {
      onModelSelect?.([]);  // Clear selection
    }
  };

  const modelColorCache = new Map();

  const getModelColor = (model) => {
    // If we already assigned a color to this model, return it
    if (modelColorCache.has(model)) {
      return modelColorCache.get(model);
    }
  
    // Check if the model name contains any of our predefined keys
    for (const [key, color] of Object.entries(MODEL_COLORS)) {
      if (model.toLowerCase().includes(key.toLowerCase())) {
        modelColorCache.set(model, color);
        return color;
      }
    }
  
    // If no match found, assign a fallback color
    const nextColor = FALLBACK_COLORS[modelColorCache.size % FALLBACK_COLORS.length];
    modelColorCache.set(model, nextColor);
    return nextColor;
  };

  const formatValue = (value) => {
    switch (displayMetric) {
      case 'cost':
        return `$${Number(value).toFixed(4)}`;
      case 'calls':
      case 'tokens':
        return Number(value).toLocaleString();
      default:
        return value;
    }
  };

  if (!chartData.length) {
    return (
      <Card>
        <Empty description="No data available" />
      </Card>
    );
  }

  return (
    <Card 
      title={
        displayMetric === 'cost' 
          ? 'Cost Over Time' 
          : displayMetric === 'calls' 
            ? 'API Calls Over Time' 
            : 'Tokens Over Time'
      }
    >
    <Spin spinning={loading}>
      <div 
        style={{ width: '100%', height: 400 }} 
        onClick={handleChartClick}
      >
        <ResponsiveContainer>
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
              <CartesianGrid strokeDasharray="3 3" opacity={0.5} />
              <XAxis 
                dataKey="date" 
                tickFormatter={(date) => new Date(date).toLocaleDateString()}
                tick={{ fill: '#8c8c8c' }}
              />
              <YAxis 
                tickFormatter={formatValue}
                tick={{ fill: '#8c8c8c' }}
              />
              <Tooltip
                formatter={(value, name) => [formatValue(value), name]}
                labelFormatter={(label) => new Date(label).toLocaleDateString()}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.96)',
                  border: '1px solid #f0f0f0',
                  borderRadius: '6px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
              />
              <Legend 
                onClick={handleLegendClick}
                wrapperStyle={{ paddingTop: '10px' }}
                formatter={(value, entry) => (
                  <span 
                    style={{ 
                      color: selectedModels.length === 0 || selectedModels.includes(value) 
                        ? '#262626' 
                        : '#bfbfbf',
                      cursor: 'pointer',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      transition: 'all 0.3s',
                      backgroundColor: selectedModels.includes(value) ? '#f5f5f5' : 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!selectedModels.includes(value)) {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!selectedModels.includes(value)) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {value}
                  </span>
                )}
              />
              {models.map((model) => (
                <Area
                  key={model}
                  type="monotone"
                  dataKey={model}
                  stackId="1"
                  stroke={getModelColor(model)}
                  fill={getModelColor(model)}
                  fillOpacity={selectedModels.length === 0 || selectedModels.includes(model) ? 0.6 : 0.1}
                  strokeOpacity={selectedModels.length === 0 || selectedModels.includes(model) ? 1 : 0.3}
                  name={model}
                  style={{ cursor: 'pointer' }}
                />
              ))}
            <Area
              type="monotone"
              dataKey="total"
              stroke="#000000"
              strokeWidth={2}
              fill="none"
              name="Total"
              strokeDasharray="5 5"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Spin>
  </Card>
  );
};

export default CostAnalyticsChart;